<script setup lang="ts">
import { computed, inject, toRef } from 'vue';
import { useFormMode } from '@/components/forms/useFormMode';
import { isCopyInjectionKey } from '../injectionKeys';
import VeeDatePicker from '@/components/forms/DatePicker/VeeDatePicker.vue';
import VeeTimePicker from '@/components/forms/TimePicker/VeeTimePicker.vue';
import VeeDateTimePicker from '@/components/forms/DateTimePicker/VeeDateTimePicker.vue';
import VeeNumberField from '@/components/forms/NumberField/VeeNumberField.vue';
import VeeSelect from '@/components/forms/Select/VeeSelect.vue';
import VeeCheckbox from '@/components/forms/Checkbox/VeeCheckbox.vue';
import { SelectItemTypeObject } from '@/components/forms/types';
import {
  DayOfWeek,
  JobFrequency as Freq,
  JobConfigurationDto,
  JobScheduleDto,
} from '@/api/generator';
import { useFormValues, useSetFieldValue, useFieldError, useField } from 'vee-validate';
import { includes } from 'lodash';
import { mdiInformation } from '@mdi/js';
import { useTranslation } from 'i18next-vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import DateTimeFormatter from '@/components/formatters/DateTimeFormatter.vue';
import { getDefaults } from '../defaults';

const { t } = useTranslation();

const ALL_WEEKDAYS: DayOfWeek[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const weekdayOptions = computed(() =>
  ALL_WEEKDAYS.map((day) => ({
    title: t(`DayOfWeek.${day}`),
    value: day,
  })),
);

const props = defineProps<{
  name: string;
}>();

const mode = useFormMode();

const isCopy = inject(
  isCopyInjectionKey,
  computed(() => false),
);

const formValues = useFormValues<JobConfigurationDto>();

const jobSchedule = computed(() => formValues.value.schedule);

const setValue = useSetFieldValue<Partial<JobScheduleDto>>(toRef(() => props.name));

const weekdaysError = useFieldError(toRef(() => `${props.name}.weekdays`));

const { value: periodStartValue } = useField<string | undefined>(
  toRef(() => `${props.name}.periodStart`),
);
const { value: periodEndValue } = useField<string | undefined>(
  toRef(() => `${props.name}.periodEnd`),
);

const freqencyOptions: SelectItemTypeObject<Freq>[] = [
  {
    title: t('FrequencyType.None'),
    value: Freq.None,
  },
  {
    title: t('FrequencyType.Daily'),
    value: Freq.Daily,
  },
  {
    title: t('FrequencyType.Weekly'),
    value: Freq.Weekly,
  },
  {
    title: t('FrequencyType.Monthly'),
    value: Freq.Monthly,
  },
  {
    title: t('FrequencyType.Yearly'),
    value: Freq.Yearly,
  },
];

const availableConfigurations = computed(() => {
  const freq = jobSchedule.value?.frequency;

  return {
    executionDate: {
      visible: includes([Freq.None, Freq.Weekly, Freq.Monthly, Freq.Yearly], freq),
    },
    executionTime: {
      disabled: jobSchedule.value?.executionAllDay,
      visible: !(mode.value === 'details' && jobSchedule.value?.executionAllDay),
    },
    executionAllDay: {
      visible: !(mode.value === 'details'),
    },
    interval: {
      visible: includes([Freq.Weekly, Freq.Monthly, Freq.Yearly], freq),
    },
    seriesPeriod: {
      visible: freq !== Freq.None,
    },
    weekdays: {
      visible: includes([Freq.Daily], freq),
      disabled: jobSchedule.value?.frequency !== Freq.Daily,
    },
  };
});

const intervalString = computed<string>(() => {
  switch (jobSchedule.value?.frequency) {
    case Freq.Weekly:
      return t('Week', { count: jobSchedule.value.interval });
    case Freq.Monthly:
      return t('Month', { count: jobSchedule.value.interval });
    case Freq.Yearly:
      return t('Year', { count: jobSchedule.value.interval });
    default:
      return '';
  }
});

const executionTimeHint = computed<string | undefined>(() => {
  switch (jobSchedule.value?.frequency) {
    case Freq.Weekly:
    case Freq.Monthly:
    case Freq.Yearly:
      return t('ExecutionTimeHint');
    default:
      return undefined;
  }
});

const periodHintString = computed<string>(() => {
  switch (jobSchedule.value?.frequency) {
    case Freq.Daily:
      return t('PeriodHint.Daily');
    case Freq.Weekly:
      return t('PeriodHint.Weekly');
    case Freq.Monthly:
      return t('PeriodHint.Monthly');
    case Freq.Yearly:
      return t('PeriodHint.Yearly');
    default:
      return '';
  }
});

function update(values: 'defaults' | 'update' = 'update') {
  const defaults = getDefaults(
    jobSchedule.value?.frequency,
    values === 'update' ? jobSchedule.value : undefined,
  );

  setValue({ ...defaults, id: jobSchedule?.value?.id });
}
</script>

<template>
  <v-row>
    <v-col cols="2">
      <v-label class="text-primary-darken-2">
        {{ $t('Recurrence') }}
      </v-label>
    </v-col>
    <v-col cols="10">
      <v-row>
        <v-col cols="3">
          <VeeSelect
            :name="`${name}.frequency`"
            :mode="mode"
            :label="$t('Frequency')"
            class="gutterTop"
            :items="freqencyOptions"
            @change="update(mode === 'add' && !isCopy ? 'defaults' : 'update')"
          />
        </v-col>

        <v-col v-if="availableConfigurations.interval.visible" cols="5">
          <div class="d-flex align-center mt-6">
            <span class="mr-2">
              {{ $t('Every') }}
            </span>
            <VeeNumberField :name="`${name}.interval`" />

            <span class="ml-2">
              {{ intervalString }}
            </span>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>

  <v-row v-if="availableConfigurations.seriesPeriod.visible">
    <v-col cols="2">
      <v-label class="text-primary-darken-2">
        {{ $t('SeriesPeriod') }}
      </v-label>

      <Tooltip>
        <template #target="{ props: tooltipProps }">
          <v-icon class="ml-2" size="x-small" v-bind="tooltipProps" :icon="mdiInformation" />
        </template>

        <template #tooltip> {{ periodHintString }} </template>
      </Tooltip>
    </v-col>

    <v-col cols="10">
      <template v-if="mode === 'details'">
        <div class="d-inline-block mr-6" style="width: 10em">
          <v-label class="text-primary-darken-2">{{ $t('StartDate') }}</v-label>
          <p class="mb-0">
            <DateTimeFormatter :value="periodStartValue" format="shortDateOnly" />
          </p>
        </div>

        <div class="d-inline-block" style="width: 10em">
          <v-label class="text-primary-darken-2">{{ $t('EndDate') }}</v-label>
          <p class="mb-0">
            <DateTimeFormatter :value="periodEndValue" format="shortDateOnly" />
          </p>
        </div>
      </template>

      <template v-else>
        <VeeDatePicker
          class="d-inline-block mr-6"
          style="width: 10em"
          format="Date"
          :name="`${name}.periodStart`"
          :dataTimeZoneName="`${name}.timeZone`"
          :label="$t('StartDate')"
        />

        <VeeDatePicker
          class="d-inline-block"
          style="width: 10em"
          format="Date"
          :name="`${name}.periodEnd`"
          :dataTimeZoneName="`${name}.timeZone`"
          :label="$t('EndDate')"
        />
      </template>
    </v-col>
  </v-row>

  <v-divider class="mt-5 mb-5" />

  <v-row v-if="availableConfigurations.weekdays.visible">
    <v-col cols="2">
      <v-label class="text-primary-darken-2">
        {{ $t('Weekdays') }}
      </v-label>
    </v-col>

    <v-col cols="10">
      <template v-if="availableConfigurations.weekdays.visible">
        <div v-if="mode !== 'details'" class="checkbox-container">
          <VeeCheckbox
            v-for="weekday in weekdayOptions"
            :key="weekday.value"
            :name="`${name}.weekdays`"
            :label="weekday.title"
            :value="weekday.value"
            :disabled="availableConfigurations.weekdays.disabled"
            :hideDetails="true"
            class="d-inline-block mr-2"
          />
          <v-alert v-if="weekdaysError" type="error" variant="tonal" class="text-body-1 mt-2">
            {{ weekdaysError }}
          </v-alert>
        </div>

        <VeeSelect v-else :name="`${name}.weekdays`" :items="weekdayOptions" multiple
      /></template>
    </v-col>
  </v-row>

  <v-row>
    <v-col cols="2">
      <v-label class="text-primary-darken-2">
        {{
          availableConfigurations.executionDate.visible
            ? $t('ExecutionTimeAndDay')
            : $t('ExecutionTime')
        }}
      </v-label>

      <Tooltip v-if="executionTimeHint">
        <template #target="{ props: tooltipProps }">
          <v-icon class="ml-2" size="x-small" v-bind="tooltipProps" :icon="mdiInformation" />
        </template>

        <template #tooltip> {{ executionTimeHint }} </template>
      </Tooltip>
    </v-col>
    <v-col cols="10">
      <template v-if="availableConfigurations.executionDate.visible">
        <VeeDateTimePicker
          class="d-inline-block mr-6"
          style="width: 10em"
          :name="`${name}.executionStart`"
          :dataTimeZoneName="`${name}.timeZone`"
          :label="$t('Start')"
          :timeDisabled="availableConfigurations.executionTime.disabled"
        />

        <VeeDateTimePicker
          class="d-inline-block mr-6"
          style="width: 10em"
          :name="`${name}.executionEnd`"
          :dataTimeZoneName="`${name}.timeZone`"
          :label="$t('End')"
          :timeDisabled="availableConfigurations.executionTime.disabled"
        />
      </template>

      <template v-else-if="availableConfigurations.executionTime.visible">
        <VeeTimePicker
          class="d-inline-block mr-6"
          style="width: 10em"
          :name="`${name}.executionStart`"
          :dataTimeZoneName="`${name}.timeZone`"
          :label="$t('Start')"
          :disabled="availableConfigurations.executionTime.disabled"
        />

        <VeeTimePicker
          class="d-inline-block mr-6"
          style="width: 10em"
          :name="`${name}.executionEnd`"
          :dataTimeZoneName="`${name}.timeZone`"
          :label="$t('End')"
          :disabled="availableConfigurations.executionTime.disabled"
        />
      </template>

      <template v-if="availableConfigurations.executionAllDay.visible">
        <VeeCheckbox
          class="d-inline-block"
          :name="`${name}.executionAllDay`"
          :label="$t('AllDay')"
          @change="update()"
        />
      </template>
    </v-col>
  </v-row>
</template>

<style lang="scss" scoped>
.checkbox-container {
  display: inline-block;
}
</style>
